import React, { useState } from "react";
import styles from "../styles/try-now.module.css"; // Ensure this path is correct
import { useNavigate } from "react-router-dom";

// --- Sub-Components ---

const Benefits = () => (
  <div className={styles.benefitsContainer}>
    <h2 className={styles.benefitsTitle}>Why Choose Us?:</h2>
    <div className={styles.benefitsGrid}>
      {/* Benefit Item 1 */}
      <div className={styles.benefitItem}>
        <div className={styles.benefitIconWrapper}>
          <i className={`fas fa-tachometer-alt ${styles.benefitIcon}`}></i>
        </div>
        <div className={styles.benefitText}>
          <span>Apply to 40+ Jobs Daily</span>
          <p>Save hours of manual application time.</p>
        </div>
      </div>
      {/* Benefit Item 2 */}
      <div className={styles.benefitItem}>
        <div className={styles.benefitIconWrapper}>
          <i className={`fas fa-shield-alt ${styles.benefitIcon}`}></i>
        </div>
        <div className={styles.benefitText}>
          <span>Keep Your Data Private</span>
          <p>Everything stays on your computer.</p>
        </div>
      </div>
      {/* Benefit Item 3 */}
      <div className={styles.benefitItem}>
        <div className={styles.benefitIconWrapper}>
          <i className={`fas fa-user-shield ${styles.benefitIcon}`}></i>
        </div>
        <div className={styles.benefitText}>
          <span>Protect Your LinkedIn</span>
          <p>Avoids detection and account restrictions.</p>
        </div>
      </div>
    </div>
  </div>
);

const ServerInfo = () => (
  <div className={styles.serverInfoCard}>
    <h3>
      <i className={`fas fa-info-circle ${styles.infoIcon}`}></i> About The Local Server
    </h3>
    <p className={styles.serverInfoIntro}>
      The local server runs on your computer to keep your job search safe and efficient.
    </p>
    <ul className={styles.serverInfoList}>
      <li>
        <i className="fas fa-lock"></i>
        <span>
          <strong>Privacy Protection:</strong> Your LinkedIn login and resume never leave your computer. We don't store or see your password.
        </span>
      </li>
      <li>
        <i className="fas fa-shield-alt"></i>
        <span>
          <strong>Account Safety:</strong> By running locally, it helps protect your LinkedIn account from restrictions or bans.
        </span>
      </li>
      <li>
        <i className="fas fa-bolt"></i>
        <span>
          <strong>Fast Applications:</strong> Apply to dozens of jobs without the tedious manual work of filling out each form.
        </span>
      </li>
      <li>
        <i className="fas fa-laptop"></i>
        <span>
          <strong>Works While You Wait:</strong> Run it in the background while you focus on other tasks.
        </span>
      </li>
    </ul>
  </div>
);

const StepIndicator = ({ currentStep }) => (
  <div className={styles.stepIndicator}>
    <div className={`${styles.step} ${currentStep >= 1 ? styles.active : ""}`}>
      <div className={styles.stepDot}>1</div>
      <div className={styles.stepLabel}>Download</div>
    </div>
    <div className={styles.stepLine}></div>
    <div className={`${styles.step} ${currentStep >= 2 ? styles.active : ""}`}>
      <div className={styles.stepDot}>2</div>
      <div className={styles.stepLabel}>Setup & Login</div>
    </div>
  </div>
);

// --- Main Component ---

const TryNow = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const navigate = useNavigate();

  // Ensure your .env setup is correct (e.g., using Create React App or Vite)
  const serverDownloadUrl = process.env.REACT_APP_SERVER_DOWNLOAD_URL || "#"; // Use '#' as fallback
  const isDownloadDisabled = !serverDownloadUrl || serverDownloadUrl === "#"; // Disable if URL is missing or fallback

  const handleDownload = () => {
    if (isDownloadDisabled) return;

    console.log("Attempting download from:", serverDownloadUrl);
    const anchor = document.createElement("a");
    anchor.href = serverDownloadUrl;
    anchor.download = "LinkedIn-Auto-Apply-Setup.zip"; // Suggest a filename
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  const goToNextStep = () => setCurrentStep(2);
  const goToPrevStep = () => setCurrentStep(1);
  const goToLogin = () => navigate("/login");
  const goHome = () => navigate("/");

  return (
    <div className={styles.pageContainer}>
      {/* Back button */}
      <button className={styles.backButton} onClick={goHome} aria-label="Go back to homepage">
        <i className="fas fa-arrow-left"></i> Back to Home
      </button>

      <div className={styles.mainWrapper}>
        {/* Sidebar Area (Order adjusted for mobile in CSS) */}
        <aside className={styles.sidebarArea}>
          <ServerInfo />
        </aside>

        {/* Main Content Area (Order adjusted for mobile in CSS) */}
        <main className={styles.contentArea}>
          <StepIndicator currentStep={currentStep} />

          <div className={styles.stepTransitionContainer}>
            {currentStep === 1 && (
              <div className={`${styles.stepContent} ${styles.fadeIn}`}>
                <img src="/icon.ico" alt="Job Assistant Logo" className={styles.logo} />
                <h1 className={styles.mainTitle}>Get Started With Linkedin Auto Apply</h1>
                <p className={styles.mainSubtitle}>
                  First, download our local server app to automate your job applications securely.
                </p>

                <button
                  onClick={handleDownload}
                  className={`${styles.actionButton} ${styles.primaryButton} ${styles.downloadButton}`}
                  disabled={isDownloadDisabled}
                >
                  <i className="fas fa-download"></i> Download Local Server
                </button>
                {isDownloadDisabled ? (
                  <p className={`${styles.downloadInfo} ${styles.errorText}`}>
                    <i className="fas fa-exclamation-triangle"></i> Download link unavailable. Please contact support or check back later.
                  </p>
                ) : (
                  <p className={styles.downloadInfo}>
                    <i className="fab fa-windows"></i> Windows only (88 MB) | Requires Google Chrome
                  </p>
                )}

                <Benefits /> {/* Benefits moved below download */}

                <button
                  onClick={goToNextStep}
                  className={`${styles.actionButton} ${styles.secondaryButton}`}
                  disabled={isDownloadDisabled}
                  title={isDownloadDisabled ? "Download is currently unavailable" : "Proceed to setup instructions"}
                >
                  I've Downloaded, Continue to Setup <i className="fas fa-arrow-right"></i>
                </button>
              </div>
            )}

            {currentStep === 2 && (
              <div className={`${styles.stepContent} ${styles.fadeIn}`}>
                <h1 className={styles.mainTitle}>Quick Setup Guide</h1>
                <p className={styles.mainSubtitle}>
                  Follow these simple steps after downloading the server:
                </p>

                <div className={styles.instructionsBox}>
                  <ol className={styles.instructionsList}>
                    <li>
                      <i className="fas fa-folder-open"></i>
                      Find & open the downloaded <code className={styles.code}>LinkedIn Auto Apply Setup.zip</code> file.
                    </li>
                    <li>
                      <i className="fas fa-file-archive"></i>
                      <strong>Extract all files</strong> (Right-click on the ZIP → "Extract All..."). Choose a location you'll remember.
                    </li>
                    <li>
                      <i className="fas fa-running"></i>
                      Open the <strong>extracted folder</strong> and double-click <code className={styles.code}>LinkedIn Auto Apply.exe</code> to run it.
                    </li>
                    <li>
                      <i className="fab fa-chrome"></i>
                      Make sure you are <strong>logged into LinkedIn</strong> in your Google Chrome browser.
                    </li>
                    <li>
                      <i className="fas fa-desktop"></i>
                      A shortcut named "LinkedIn Auto Apply" should appear on your Desktop for easy access next time.
                    </li>
                  </ol>
                  <p className={styles.note}>
                    <i className="fas fa-exclamation-circle"></i>
                    <strong>Important:</strong> The local server application must be running in the background on your computer whenever you want to apply for jobs.
                  </p>
                </div>

                <div className={styles.buttonGroup}>
                  <button
                    onClick={goToPrevStep}
                    className={`${styles.actionButton} ${styles.secondaryButton}`}
                  >
                    <i className="fas fa-arrow-left"></i> Back to Download
                  </button>
                  <button
                    onClick={goToLogin}
                    className={`${styles.actionButton} ${styles.launchButton}`} // Using launchButton style (green)
                  >
                    Continue to Login <i className="fas fa-sign-in-alt"></i>
                  </button>
                </div>
              </div>
            )}
          </div> {/* End Step Transition Container */}
        </main>
      </div>
    </div>
  );
};

export default TryNow;